/**
 * Classify a MF scheme_name into an SEBI-style sub-category.
 * Order matters — the most specific pattern must match first.
 * Shared between Sub-Category Allocation card and future donut chart.
 */
export const classifySubCategory = (name) => {
  const s = (name || "").toLowerCase();
  if (!s) return "Other";
  // Equity sub-categories
  if (/\belss\b|tax\s*saver/.test(s)) return "Equity - ELSS";
  if (/large\s*&?\s*mid|large\s*and\s*mid/.test(s)) return "Equity - Large & Mid Cap";
  if (/flexi\s*cap/.test(s)) return "Equity - Flexi Cap";
  if (/multi\s*cap/.test(s)) return "Equity - Multi Cap";
  if (/small\s*cap/.test(s)) return "Equity - Small Cap";
  if (/mid\s*cap/.test(s)) return "Equity - Mid Cap";
  if (/large\s*cap|bluechip/.test(s)) return "Equity - Large Cap";
  if (/focused/.test(s)) return "Equity - Focused";
  if (/value|contra/.test(s)) return "Equity - Value / Contra";
  if (/dividend\s*yield/.test(s)) return "Equity - Dividend Yield";
  if (/sector|infrastructure|banking|pharma|technology|energy|consumption|manufacturing|healthcare/.test(s)) return "Equity - Sectoral Fund - Infrastructure";
  if (/thematic|business\s*cycle|opportunities/.test(s)) return "Equity - Thematic Fund - Other";
  if (/index|nifty|sensex|etf/.test(s)) return "Equity - Index / ETF";
  // Hybrid sub-categories
  if (/balanced\s*advantage|dynamic\s*asset/.test(s)) return "Hybrid - Balanced Advantage";
  if (/multi\s*asset/.test(s)) return "Hybrid - Multi Asset Allocation";
  if (/aggressive\s*hybrid|equity\s*savings/.test(s)) return "Hybrid - Aggressive";
  if (/conservative\s*hybrid/.test(s)) return "Hybrid - Conservative";
  if (/arbitrage/.test(s)) return "Hybrid - Arbitrage";
  if (/dynamic\s*hybrid/.test(s)) return "Hybrid - Dynamic Asset Allocation";
  // Debt sub-categories
  if (/liquid/.test(s)) return "Debt - Liquid";
  if (/overnight/.test(s)) return "Debt - Overnight";
  if (/ultra\s*short/.test(s)) return "Debt - Ultra Short";
  if (/low\s*duration/.test(s)) return "Debt - Low Duration";
  if (/short\s*(term|duration)/.test(s)) return "Debt - Short Duration";
  if (/medium\s*(term|duration)/.test(s)) return "Debt - Medium Duration";
  if (/long\s*(term|duration)/.test(s)) return "Debt - Long Duration";
  if (/corporate\s*bond/.test(s)) return "Debt - Corporate Bond";
  if (/banking\s*&?\s*psu|banking\s*and\s*psu/.test(s)) return "Debt - Banking & PSU";
  if (/gilt/.test(s)) return "Debt - Gilt";
  if (/credit\s*risk/.test(s)) return "Debt - Credit Risk";
  if (/money\s*market/.test(s)) return "Debt - Money Market";
  if (/dynamic\s*bond/.test(s)) return "Debt - Dynamic Bond";
  // FoF / Commodity
  if (/gold.*silver|silver.*gold/.test(s)) return "FoFs (Domestic / Overseas) - Gold & Silver";
  if (/\bgold\b/.test(s)) return "FoFs (Domestic / Overseas) - Gold";
  if (/\bsilver\b/.test(s)) return "FoFs (Domestic / Overseas) - Silver";
  if (/fof.*overseas|international|us\s*equity|global|nasdaq|s&p\s*500/.test(s)) return "FoFs (Domestic / Overseas) - International";
  if (/fof|fund of fund/.test(s)) return "FoFs (Domestic / Overseas) - Hybrid";
  return "Other";
};

/**
 * Parse a `DD-MM-YYYY` (Karvy/CAMS) or ISO date string to a JS Date.
 * Returns null for invalid input.
 */
export const parseDDMMYYYY = (ds) => {
  if (!ds) return null;
  const parts = String(ds).split("-");
  if (parts.length === 3 && parts[0].length === 2) return new Date(parts[2], parts[1] - 1, parts[0]);
  return new Date(ds);
};

/**
 * Compute cost, market and dated cashflows for one active-units transaction.
 * Shared helper used by Sub-Category and Family Allocation cards so their
 * numbers agree with the XIRR-basis-holding-period card in Portfolio.js.
 */
export const computeLotEconomics = (txn) => {
  const originalUnits = Math.abs(parseFloat(txn.units || txn.original_units || 0));
  const remainingUnits =
    txn.remaining_units != null && txn.remaining_units !== ""
      ? Math.max(0, parseFloat(txn.remaining_units) || 0)
      : Math.max(0, originalUnits - Math.abs(parseFloat(txn.units_sold || txn.sold_units || 0)));
  const stampDuty = parseFloat(txn.stamp_duty || 0);
  const propStamp = originalUnits > 0 && stampDuty > 0 ? (remainingUnits / originalUnits) * stampDuty : 0;
  const cost = remainingUnits * (parseFloat(txn.nav) || 0) + propStamp;
  const currentNavObj = txn.current_nav;
  const currentNav = typeof currentNavObj === "object" ? currentNavObj?.nav || 0 : currentNavObj || txn.nav || 0;
  const market = remainingUnits * (parseFloat(currentNav) || 0);
  const d = parseDDMMYYYY(txn.transaction_date || txn.trade_date);
  return { cost, market, dateObj: d };
};

/**
 * Bucket a scheme_name into one of the five top-level Cashflow-Snapshot
 * categories (Equity / Debt / Hybrid / Commodity / Others).
 * Derived from the more granular `classifySubCategory` above.
 */
export const classifyCategory = (name) => {
  const sub = classifySubCategory(name);
  if (sub.startsWith("Equity")) return "Equity";
  if (sub.startsWith("Debt")) return "Debt";
  if (sub.startsWith("Hybrid")) return "Hybrid";
  if (/Gold|Silver/.test(sub)) return "Commodity";
  return "Others";
};

/**
 * True Newton-Raphson XIRR over an array of dated cashflows.
 * Returns annualized rate as a percentage (e.g. 12.5 for +12.5%).
 *
 * Cashflow convention: outflows (real client cash leaving) are NEGATIVE,
 * inflows (real client cash returning) are POSITIVE. Terminal
 * `{ date: today, amount: +market_value }` closes the position.
 *
 * NOTE: Switch In / Switch Out are internal fund-to-fund reallocations
 * (no client cash movement) and MUST NOT be pushed into `cashflows` —
 * otherwise Newton-Raphson diverges (500% cap hit).
 *
 * Adds a bisection fallback when Newton pushes r out of [-0.99, 5].
 */
export const xirr = (cashflows) => {
  if (!cashflows || cashflows.length < 2) return 0;
  const cfs = cashflows
    .filter((cf) => cf && cf.date && !isNaN(cf.date.getTime()) && Number.isFinite(cf.amount) && cf.amount !== 0)
    .slice()
    .sort((a, b) => a.date - b.date);
  if (cfs.length < 2) return 0;
  if (!cfs.some((cf) => cf.amount < 0) || !cfs.some((cf) => cf.amount > 0)) return 0;

  const t0 = cfs[0].date.getTime();
  const years = cfs.map((cf) => (cf.date.getTime() - t0) / (365.25 * 24 * 3600 * 1000));
  const amts = cfs.map((cf) => cf.amount);

  const npv = (r) => {
    let s = 0;
    for (let i = 0; i < amts.length; i++) s += amts[i] / Math.pow(1 + r, years[i]);
    return s;
  };
  const dnpv = (r) => {
    let s = 0;
    for (let i = 0; i < amts.length; i++) s += -amts[i] * years[i] / Math.pow(1 + r, years[i] + 1);
    return s;
  };

  // Newton-Raphson with damping.
  let r = 0.1;
  for (let iter = 0; iter < 100; iter++) {
    const f = npv(r);
    const df = dnpv(r);
    if (!Number.isFinite(f) || !Number.isFinite(df) || Math.abs(df) < 1e-12) break;
    let dr = f / df;
    if (dr > 1) dr = 1;
    if (dr < -1) dr = -1;
    const newR = r - dr;
    if (Math.abs(newR - r) < 1e-8) { r = newR; break; }
    r = newR;
    if (r <= -0.99) r = -0.99;
    if (r > 5) r = 5;
  }

  // Bisection fallback if Newton failed (r out of realistic range).
  if (!Number.isFinite(r) || r < -0.99 || r > 4.99 || Math.abs(npv(r)) > 1) {
    let lo = -0.9, hi = 5;
    let fLo = npv(lo), fHi = npv(hi);
    if (Number.isFinite(fLo) && Number.isFinite(fHi) && fLo * fHi < 0) {
      for (let i = 0; i < 100; i++) {
        const mid = (lo + hi) / 2;
        const fm = npv(mid);
        if (!Number.isFinite(fm)) break;
        if (Math.abs(fm) < 1e-6 || (hi - lo) < 1e-8) { r = mid; break; }
        if (fm * fLo < 0) { hi = mid; fHi = fm; }
        else { lo = mid; fLo = fm; }
      }
    }
  }

  const pct = r * 100;
  if (!Number.isFinite(pct)) return 0;
  if (pct > 200) return 200;
  if (pct < -95) return -95;
  return pct;
};

/**
 * Compute true annualized XIRR for a holding given its cost/market and the
 * dated outflow cashflows. Appends terminal (today, +market) automatically.
 * Returns 0 when there's not enough signal.
 */
export const weightedDaysXirr = (cost, market, cashflows) => {
  if (cost <= 0 || market <= 0 || !cashflows || cashflows.length === 0) return 0;
  const today = new Date();
  const allCfs = [...cashflows, { date: today, amount: market }];
  return xirr(allCfs);
};
